import { Loan } from '@/types/loan';
import { LoanCreate, LoanByIdResponse } from '@/types/api.types';

// Interface for the nested API response structure
interface NestedLoanResponse {
  loan_identification?: {
    rp_system_id?: number;
    client_loan_number?: string;
    loan_name?: string;
    sponsor_borrower?: string;
  };
  property_details?: {
    sector?: string;
    property_sector?: string;
    property_type?: string;
    property_lifecycle_financing?: string;
    noi?: number;
    property_appraisal_value?: number;
    property_appraisal_value_date?: string;
    ltv_current?: number;
    dscr_current?: number;
    debt_yield_current?: number;
    locations?: Array<{
      street?: string;
      city?: string;
      state?: string;
      zip_code?: string;
      country?: string;
      region?: string;
    }>;
  };
  financial_terms?: {
    original_balance?: number;
    current_balance?: number;
    currency?: string;
    client_percentage?: number;
    pik_balance?: string;
    credit_spread?: number;
    position_in_capital_stack?: string;
    amortization_type?: string;
    periodicity?: string;
    interest_day_count?: string;
    io_end_date?: string;
    original_amortization_term?: number;
    contractual_pi_payment_amount?: number;
    accrual_type?: string;
    pik_coupon?: number;
    commitment_type?: string;
    unfunded_commitment_fee?: number;
    interest_type?: string;
    interest_rate?: number;
    fixed_rate_coupon?: number;
    floating_rate_index?: string;
    floating_rate_margin?: number;
    index_cap?: number;
    index_floor?: number;
    maturity_date?: string;
  };
  risk_metrics?: {
    pd?: number;
    ead?: number;
    lgd?: number;
    lag_to_recovery?: number;
    default_date?: string;
    cdr?: number;
    ltv?: number;
    dscr?: number;
    debt_yield?: number;
  };
  key_dates?: {
    origination_date?: string;
    first_payment_date?: string;
    original_maturity_date?: string;
    prepayment_lockout_end_date?: string;
    open_call_period_date?: string;
    first_extension_date?: string;
    first_extension_fee?: number;
    second_extension_date?: string;
    second_extension_fee?: number;
    third_extension_date?: string;
    third_extension_fee?: number;
    exit_fee?: number;
  };
  loan_status?: {
    loan_status?: string;
    commentary?: string;
    internal_credit_rating?: string;
    watchlist_monitoring?: string;
  };
  property_locations?: Array<{
    street?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    country?: string;
    region?: string;
  }>;
  pricing_results?: {
    market_yield?: number;
  };
  pricing_scenario?: string;
  maturity_assumption?: string;
  market_yield?: number;
  loss_scenario?: string;
  // Top-level date fields (flat responses)
  origination_date?: string;
  first_payment_date?: string;
  original_maturity_date?: string;
  prepayment_lockout_end_date?: string | null;
  open_call_period_date?: string | null;
  first_extension_date?: string | null;
  first_extension_fee?: number | null;
  second_extension_date?: string | null;
  second_extension_fee?: number | null;
  third_extension_date?: string | null;
  third_extension_fee?: number | null;
  exit_fee?: number | null;
  step_up_date?: string;
  step_up_incremental_rate?: number;
  step_up_coupons?: string;
  step_up_margin_interest_rate_floor?: string;
  prepayment_penalty_type?: string;
  prepayment_penalty_description?: string;
  in_forbearance?: string;
  forbearance_start_date?: string;
  forbearance_original_term?: number;
  forbearance_type?: string;
  forbearance_payback_start_date?: string;
  forbearance_payback_term?: number;
  pi_scheduled_amortization?: string;
  custom_payment_dates_schedule?: string;
  default_interest_schedule?: string;
  preferred_equity_equity_kicker_schedule?: string;
  proforma_assumptions?: string;
  custom_interest_type_timing?: string;
  x1?: string;
  x2?: string;
  x3?: string;
  // Top-level property_appraisal_value (from LoanByIdResponse)
  property_appraisal_value?: number | null;
}

export function mapFrontendToBackend(loan: Loan): LoanCreate {
  return {
    // Basic fields
    rp_system_id: loan.system_id,
    pricing_scenario: loan.pricing_scenario,
    maturity_assumption: loan.maturity_assumption || null,
    credit_spread: loan.credit_spread,
    market_yield: loan.market_yield || null,
    loss_scenario: loan.loss_scenario || null,

    // Loan identification
    client_loan_number: loan.loan_identification.client_loan_number,
    loan_name: loan.loan_identification.loan_name,

    // Property details
    property_sector: loan.property_details.sector,
    property_type: loan.property_details.property_type,
    property_lifecycle_financing:
      loan.property_details.property_lifecycle_financing,

    // Borrower
    sponsor_borrower: loan.borrower.sponsor_borrower,

    // Loan amounts
    original_balance: loan.loan_amounts.original_balance,
    current_balance: loan.loan_amounts.current_balance,
    currency: loan.loan_amounts.currency,
    client_percentage: loan.loan_amounts.client_percentage,
    pik_balance: loan.loan_amounts.pik_balance || null,

    // Risk metrics (optional fields)
    pd: loan.risk_metrics?.pd || null,
    ead: loan.risk_metrics?.ead || null,
    lgd: loan.risk_metrics?.lgd || null,
    lag_to_recovery: loan.risk_metrics?.lag_to_recovery || null,
    default_date: loan.risk_metrics?.default_date || null,
    cdr: loan.risk_metrics?.cdr || null,

    // Loan structure
    position_in_capital_stack: loan.loan_structure.position_in_capital_stack,
    amortization_type: loan.loan_structure.amortization_type,
    periodicity: loan.loan_structure.periodicity,
    interest_day_count: loan.loan_structure.interest_day_count,
    io_end_date: loan.loan_structure.io_end_date || null,
    original_amortization_term:
      loan.loan_structure.original_amortization_term || null,
    contractual_pi_payment_amount:
      loan.loan_structure.contractual_pi_payment_amount || null,
    accrual_type: loan.loan_structure.accrual_type,
    pik_coupon: loan.loan_structure.pik_coupon || null,
    commitment_type: loan.loan_structure.commitment_type,
    unfunded_commitment_fee:
      loan.loan_structure.unfunded_commitment_fee || null,

    // Interest terms
    interest_type: loan.interest_terms.interest_type,
    fixed_rate_coupon: loan.interest_terms.fixed_rate_coupon || null,
    floating_rate_index: loan.interest_terms.floating_rate_index || null,
    floating_rate_margin: loan.interest_terms.floating_rate_margin || null,
    index_cap: loan.interest_terms.index_cap || null,
    index_floor: loan.interest_terms.index_floor || null,

    // Property metrics
    ltv_current: loan.property_metrics?.ltv_current || null,
    dscr_current: loan.property_metrics?.dscr_current || null,
    debt_yield_current: loan.property_metrics?.debt_yield_current || null,
    noi: loan.property_metrics?.noi || null,
    property_appraisal_value:
      loan.property_metrics?.property_appraisal_value || null,
    property_appraisal_value_date:
      loan.property_metrics?.property_appraisal_value_date || null,

    // Dates
    origination_date: loan.dates.origination_date,
    first_payment_date: loan.dates.first_payment_date,
    original_maturity_date: loan.dates.original_maturity_date,
    prepayment_lockout_end_date: loan.dates.prepayment_lockout_end_date || null,
    open_call_period_date: loan.dates.open_call_period_date || null,
    first_extension_date: loan.dates.first_extension_date || null,
    first_extension_fee: loan.dates.first_extension_fee || null,
    second_extension_date: loan.dates.second_extension_date || null,
    second_extension_fee: loan.dates.second_extension_fee || null,
    third_extension_date: loan.dates.third_extension_date || null,
    third_extension_fee: loan.dates.third_extension_fee || null,
    exit_fee: loan.dates.exit_fee || null,

    // Loan status
    loan_status: loan.loan_status.status,
    commentary: loan.loan_status.commentary,
    internal_credit_rating: loan.loan_status.internal_credit_rating || null,
    watchlist_monitoring: loan.loan_status.watchlist_monitoring || null,

    // Property locations (array mapping)
    property_locations: loan.property_locations.map((loc) => ({
      street: loc.street,
      city: loc.city,
      state: loc.state,
      zip_code: loc.zip_code || null,
      country: loc.country,
      region: loc.region,
    })),

    // Step-up terms
    step_up_date: loan.step_up_terms?.step_up_date || null,
    step_up_incremental_rate:
      loan.step_up_terms?.step_up_incremental_rate || null,

    // Prepayment terms
    prepayment_penalty_type:
      loan.prepayment_terms?.prepayment_penalty_type === undefined
        ? null
        : loan.prepayment_terms.prepayment_penalty_type,
    prepayment_penalty_description:
      loan.prepayment_terms?.prepayment_penalty_description || null,

    // Forbearance
    in_forbearance: loan.forbearance?.in_forbearance || null,
    forbearance_start_date: loan.forbearance?.forbearance_start_date || null,
    forbearance_original_term:
      loan.forbearance?.forbearance_original_term || null,
    forbearance_type: loan.forbearance?.forbearance_type || null,
    forbearance_payback_start_date:
      loan.forbearance?.forbearance_payback_start_date || null,
    forbearance_payback_term:
      loan.forbearance?.forbearance_payback_term || null,

    // Schedules
    pi_scheduled_amortization:
      loan.schedules?.pi_scheduled_amortization || null,
    custom_payment_dates_schedule:
      loan.schedules?.custom_payment_dates_schedule || null,
    default_interest_schedule:
      loan.schedules?.default_interest_schedule || null,
    preferred_equity_equity_kicker_schedule:
      loan.schedules?.preferred_equity_equity_kicker_schedule || null,

    // Step-up provisions
    step_up_coupons: loan.step_up_provisions?.step_up_coupons || null,
    step_up_margin_interest_rate_floor:
      loan.step_up_provisions?.step_up_margin_interest_rate_floor || null,

    // Credit utilization
    proforma_assumptions: loan.credit_utilization?.proforma_assumptions || null,
    custom_interest_type_timing:
      loan.credit_utilization?.custom_interest_type_timing || null,

    // Additional fields
    x1: loan.additional_fields?.x1 || null,
    x2: loan.additional_fields?.x2 || null,
    x3: loan.additional_fields?.x3 || null,
  };
}

export function mapBackendToFrontend(loanResponse: NestedLoanResponse): Loan {
  // Handle nested API response structure
  const loanId = loanResponse.loan_identification || {};
  const propertyDetails = loanResponse.property_details || {};
  const financialTerms = loanResponse.financial_terms || {};
  const riskMetrics = loanResponse.risk_metrics || {};
  const keyDates = loanResponse.key_dates || {};
  const loanStatus = loanResponse.loan_status || {};
  const propertyLocations =
    loanResponse.property_locations || propertyDetails.locations || [];
  const pricingResults = loanResponse.pricing_results || {};
  
  // Try to find currency from various possible locations in the response
  const findCurrency = (): string | undefined => {
    // Try financial_terms.currency first
    if (financialTerms.currency) return financialTerms.currency;
    
    // Try top-level currency
    if ((loanResponse as any).currency) return (loanResponse as any).currency;
    
    // Try to find currency anywhere in the response recursively
    const searchForCurrency = (obj: any, depth = 0): string | undefined => {
      if (depth > 5) return undefined; // Prevent infinite recursion
      if (!obj || typeof obj !== 'object') return undefined;
      
      if (obj.currency && typeof obj.currency === 'string') {
        return obj.currency;
      }
      
      for (const key in obj) {
        if (key === 'currency' && typeof obj[key] === 'string') {
          return obj[key];
        }
        if (typeof obj[key] === 'object' && obj[key] !== null) {
          const found = searchForCurrency(obj[key], depth + 1);
          if (found) return found;
        }
      }
      return undefined;
    };
    
    return searchForCurrency(loanResponse);
  };
  
  const foundCurrency = findCurrency();
  
  // Debug: log currency sources
  console.log('Currency sources:', {
    financialTerms_currency: financialTerms.currency,
    loanResponse_currency: (loanResponse as any).currency,
    foundCurrency: foundCurrency,
    full_loanResponse: loanResponse
  });

  return {
    // Basic fields
    // Prefer nested rp_system_id from loan_identification, but fall back to
    // flat rp_system_id that comes from LoanByIdResponse
    system_id:
      loanId.rp_system_id ??
      (loanResponse as Partial<LoanByIdResponse>).rp_system_id ??
      0,
    pricing_scenario: (loanResponse.pricing_scenario || 'RPX Pricing') as
      | 'RPX Pricing'
      | 'Custom Scenario',
    maturity_assumption: loanResponse.maturity_assumption,
    // Credit spread: check if it's in basis points (10000 = 100%) or percentage (100 = 100%) or decimal (1.0 = 100%)
    // FormattedNumberInput expects decimal (0.01 = 1%), so convert accordingly
    credit_spread: (() => {
      const val = financialTerms.credit_spread;
      if (val === null || val === undefined) return 0;
      // If value >= 1000, likely basis points (10000 bps = 100%), convert to decimal (1.0)
      if (val >= 1000) {
        return val / 10000;
      }
      // If value >= 1 and < 1000, likely percentage (100 = 100%), convert to decimal (1.0)
      if (val >= 1) {
        return val / 100;
      }
      // Otherwise assume it's already decimal (0.01 = 1%)
      return val;
    })(),
    // Market yield: same conversion
    market_yield: (() => {
      const val = pricingResults.market_yield || loanResponse.market_yield;
      if (val === null || val === undefined) return undefined;
      // Same logic: if >= 1, convert from percentage to decimal
      const result = val >= 1 ? val / 100 : val;
      console.log('Market yield conversion:', { original: val, converted: result });
      return result;
    })(),
    loss_scenario: loanResponse.loss_scenario as
      | 'PD'
      | 'No Loss'
      | null
      | undefined,

    // Risk metrics
    risk_metrics: {
      pd: riskMetrics.pd,
      ead: riskMetrics.ead,
      lgd: riskMetrics.lgd,
      lag_to_recovery: riskMetrics.lag_to_recovery,
      default_date: riskMetrics.default_date,
      cdr: riskMetrics.cdr,
    },

    // Loan identification
    loan_identification: {
      client_loan_number: loanId.client_loan_number || '',
      loan_name: loanId.loan_name || '',
    },

    // Property details
    property_details: {
      sector: (propertyDetails.sector ||
        propertyDetails.property_sector ||
        'Multifamily') as
        | 'Multifamily'
        | 'Office'
        | 'Retail'
        | 'Industrial'
        | 'Hotel'
        | 'Other',
      property_type: propertyDetails.property_type || '',
      property_lifecycle_financing:
        (propertyDetails.property_lifecycle_financing || 'Permanent') as
          | 'Permanent'
          | 'Construction'
          | 'Bridge'
          | 'Refinance',
    },

    // Borrower
    borrower: {
      sponsor_borrower: loanId.sponsor_borrower || '',
    },

    // Loan amounts
    loan_amounts: {
      original_balance: financialTerms.original_balance || 0,
      current_balance: financialTerms.current_balance || 0,
      currency: (foundCurrency || 'USD') as
        | 'USD'
        | 'EUR'
        | 'GBP'
        | 'CAD'
        | 'AUD',
      // Client percentage: if >= 1, assume percentage (100 = 100%), convert to decimal (1.0)
      // Default is 100% = 1.0 in decimal
      client_percentage: (() => {
        const val = financialTerms.client_percentage;
        if (val === null || val === undefined) return 1.0; // Default 100% = 1.0
        // If value >= 1, convert from percentage to decimal (100% -> 1.0)
        // If value < 1, assume already decimal (0.5 = 50%)
        return val >= 1 ? val / 100 : val;
      })(),
      pik_balance: financialTerms.pik_balance || '',
    },

    // Loan structure
    loan_structure: {
      position_in_capital_stack: (financialTerms.position_in_capital_stack ||
        'Senior Loan') as
        | 'Senior Loan'
        | 'Senior Loan - A Note'
        | 'Senior Loan - B Note'
        | 'Senior Loan - C Note'
        | 'Mezzanine'
        | 'IO',
      amortization_type: (financialTerms.amortization_type || 'Amortizing') as
        | 'Amortizing'
        | 'IO'
        | 'IOThenAmortizing',
      periodicity: (financialTerms.periodicity || 'Monthly') as
        | 'Monthly'
        | 'Quarterly'
        | 'Semi-Annual'
        | 'Annual',
      interest_day_count: (financialTerms.interest_day_count || '30/360') as
        | '30/360'
        | 'ACT/360'
        | 'ACT/365'
        | '30/365',
      io_end_date: financialTerms.io_end_date,
      original_amortization_term: financialTerms.original_amortization_term,
      contractual_pi_payment_amount:
        financialTerms.contractual_pi_payment_amount,
      accrual_type: (financialTerms.accrual_type || 'Accrued Interest') as
        | 'Accrued Interest'
        | 'Cash Interest',
      pik_coupon: financialTerms.pik_coupon,
      commitment_type: (financialTerms.commitment_type || 'Closed End') as
        | 'Closed End'
        | 'Open End'
        | 'Revolving',
      unfunded_commitment_fee: financialTerms.unfunded_commitment_fee,
    },

    // Interest terms
    interest_terms: {
      interest_type: (financialTerms.interest_type || 'Fixed') as
        | 'Fixed'
        | 'Floating',
      fixed_rate_coupon:
        financialTerms.fixed_rate_coupon || financialTerms.interest_rate,
      floating_rate_index: financialTerms.floating_rate_index as
        | 'SOFR1M'
        | 'LIBOR1M'
        | 'LIBOR3M'
        | 'Prime'
        | null
        | undefined,
      floating_rate_margin: financialTerms.floating_rate_margin,
      index_cap: financialTerms.index_cap,
      index_floor: financialTerms.index_floor,
    },

    // Property metrics
    property_metrics: {
      ltv_current: riskMetrics.ltv || propertyDetails.ltv_current,
      dscr_current: riskMetrics.dscr || propertyDetails.dscr_current,
      debt_yield_current:
        riskMetrics.debt_yield || propertyDetails.debt_yield_current,
      noi: propertyDetails.noi,
      // Prefer top-level property_appraisal_value (from LoanByIdResponse),
      // fall back to nested property_details.property_appraisal_value
      property_appraisal_value:
        (loanResponse as Partial<LoanByIdResponse>).property_appraisal_value ??
        loanResponse.property_appraisal_value ??
        propertyDetails.property_appraisal_value ??
        null,
      property_appraisal_value_date:
        propertyDetails.property_appraisal_value_date,
    },

    // Dates
    dates: {
      // Prefer nested key_dates (if present), otherwise fall back to flat top-level fields
      origination_date:
        keyDates.origination_date ?? loanResponse.origination_date ?? '',
      first_payment_date:
        keyDates.first_payment_date ?? loanResponse.first_payment_date ?? '',
      original_maturity_date:
        keyDates.original_maturity_date ??
        loanResponse.original_maturity_date ??
        financialTerms.maturity_date ??
        '',
      prepayment_lockout_end_date:
        keyDates.prepayment_lockout_end_date ??
        loanResponse.prepayment_lockout_end_date ??
        null,
      open_call_period_date:
        keyDates.open_call_period_date ??
        loanResponse.open_call_period_date ??
        null,
      first_extension_date:
        keyDates.first_extension_date ?? loanResponse.first_extension_date ?? null,
      first_extension_fee:
        keyDates.first_extension_fee ?? loanResponse.first_extension_fee ?? null,
      second_extension_date:
        keyDates.second_extension_date ?? loanResponse.second_extension_date ?? null,
      second_extension_fee:
        keyDates.second_extension_fee ?? loanResponse.second_extension_fee ?? null,
      third_extension_date:
        keyDates.third_extension_date ?? loanResponse.third_extension_date ?? null,
      third_extension_fee:
        keyDates.third_extension_fee ?? loanResponse.third_extension_fee ?? null,
      exit_fee: keyDates.exit_fee ?? loanResponse.exit_fee ?? null,
    },

    // Loan status
    loan_status: {
      status: (loanStatus.loan_status || 'Performing') as
        | 'Performing'
        | '30–59 Days Delinquent'
        | '60–89 Days Delinquent'
        | '90+ Days Delinquent'
        | 'Default'
        | 'Foreclosure'
        | 'REO'
        | 'Forbearance',
      commentary: loanStatus.commentary || '',
      internal_credit_rating: loanStatus.internal_credit_rating as
        | 'AAA'
        | 'AA'
        | 'A'
        | 'BBB'
        | 'BB'
        | 'B'
        | 'CCC'
        | 'CC'
        | 'C'
        | 'D'
        | null
        | undefined,
      watchlist_monitoring: loanStatus.watchlist_monitoring as
        | 'Y'
        | 'N'
        | null
        | undefined,
    },

    // Property locations
    property_locations:
      propertyLocations.length > 0
        ? propertyLocations.map((loc) => ({
            street: loc.street || '',
            city: loc.city || '',
            state: loc.state || '',
            zip_code: loc.zip_code || null,
            country: loc.country || 'United States',
            region: loc.region || 'United States',
          }))
        : [
            {
              street: '',
              city: '',
              state: '',
              zip_code: null,
              country: 'United States',
              region: 'United States',
            },
          ],

    // Step-up terms
    step_up_terms: {
      step_up_date: loanResponse.step_up_date || null,
      step_up_incremental_rate: loanResponse.step_up_incremental_rate || null,
    },

    // Step-up provisions
    step_up_provisions: {
      step_up_coupons: loanResponse.step_up_coupons || null,
      step_up_margin_interest_rate_floor:
        loanResponse.step_up_margin_interest_rate_floor || null,
    },

    // Prepayment terms
    prepayment_terms: {
      prepayment_penalty_type:
        loanResponse.prepayment_penalty_type === undefined
          ? null
          : (loanResponse.prepayment_penalty_type as
              | 'Yield Maintenance'
              | 'Fixed'
              | 'Defeasance'
              | null),
      prepayment_penalty_description:
        loanResponse.prepayment_penalty_description || null,
    },

    // Forbearance
    forbearance: {
      in_forbearance: loanResponse.in_forbearance || null,
      forbearance_start_date: loanResponse.forbearance_start_date || null,
      forbearance_original_term: loanResponse.forbearance_original_term || null,
      forbearance_type: loanResponse.forbearance_type as
        | 'P&I'
        | 'P&I Reduced'
        | 'Interest Only'
        | null
        | undefined,
      forbearance_payback_start_date:
        loanResponse.forbearance_payback_start_date || null,
      forbearance_payback_term: loanResponse.forbearance_payback_term || null,
    },

    // Schedules
    schedules: {
      pi_scheduled_amortization: loanResponse.pi_scheduled_amortization || null,
      custom_payment_dates_schedule:
        loanResponse.custom_payment_dates_schedule || null,
      default_interest_schedule: loanResponse.default_interest_schedule || null,
      preferred_equity_equity_kicker_schedule:
        loanResponse.preferred_equity_equity_kicker_schedule || null,
    },

    // Credit utilization
    credit_utilization: {
      proforma_assumptions: loanResponse.proforma_assumptions || null,
      custom_interest_type_timing:
        loanResponse.custom_interest_type_timing || null,
    },

    // Additional fields
    additional_fields: {
      x1: loanResponse.x1 || null,
      x2: loanResponse.x2 || null,
      x3: loanResponse.x3 || null,
    },
  };
}
